import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  readStoredCountry,
  resolveContent,
  writeStoredCountry,
  type CountryCode,
  type SiteContent,
} from '../../content/siteContent';

export type GeoState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  countryCode: CountryCode;
  content: SiteContent;
  source: 'stored' | 'api' | 'fallback';
};

/** IP-based country — no browser Geolocation permission; never uses URL query */
export const detectCountry = createAsyncThunk('geo/detect', async () => {
  const stored = readStoredCountry();
  if (stored) return { code: stored, source: 'stored' as const };

  try {
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:1700';
    const res = await fetch(`${base}/geo`, { credentials: 'omit' });
    if (res.ok) {
      const data = (await res.json()) as { country?: string };
      if (data.country) {
        const resolved = resolveContent(data.country);
        if (resolved.countryCode !== 'DEFAULT') {
          return { code: resolved.countryCode, source: 'api' as const };
        }
      }
    }
  } catch {
    /* fall through */
  }

  try {
    const res = await fetch('https://ipapi.co/json/');
    if (res.ok) {
      const data = (await res.json()) as { country_code?: string };
      const resolved = resolveContent(data.country_code);
      return {
        code: resolved.countryCode === 'DEFAULT' ? ('DEFAULT' as CountryCode) : resolved.countryCode,
        source: 'api' as const,
      };
    }
  } catch {
    /* fall through */
  }

  return { code: 'DEFAULT' as CountryCode, source: 'fallback' as const };
});

function contentByCountrySafe(): SiteContent {
  return resolveContent(readStoredCountry() || 'DEFAULT');
}

const initialContent = contentByCountrySafe();

const initialState: GeoState = {
  status: 'idle',
  countryCode: initialContent.countryCode,
  content: initialContent,
  source: 'fallback',
};

const geoSlice = createSlice({
  name: 'geo',
  initialState,
  reducers: {
    setCountry(state, action: PayloadAction<string>) {
      const content = resolveContent(action.payload);
      writeStoredCountry(content.countryCode);
      state.countryCode = content.countryCode;
      state.content = content;
      state.status = 'ready';
      state.source = 'stored';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(detectCountry.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(detectCountry.fulfilled, (state, action) => {
        const content = resolveContent(action.payload.code);
        state.countryCode = content.countryCode;
        state.content = content;
        state.source = action.payload.source;
        state.status = 'ready';
      })
      .addCase(detectCountry.rejected, (state) => {
        state.status = 'error';
        state.content = resolveContent('DEFAULT');
        state.countryCode = 'DEFAULT';
      });
  },
});

export const { setCountry } = geoSlice.actions;
export default geoSlice.reducer;
